import { CLIENT_TIERS, type ClientTier, clientTierLabel, isClientTier, UNCATEGORIZED_LABEL } from "@klarity/schema";
import { Link } from "@tanstack/react-router";
import { Lock } from "lucide-react";
import { type MutableRefObject, useEffect, useRef, useState } from "react";
import { EmptyState, PageTitle, Spinner } from "../../components/ui/misc";
import { Crumb, Page, PageBar } from "../../components/ui/PageBar";
import { Panel } from "../../components/ui/Panel";
import { Select, type SelectOption } from "../../components/ui/Select";
import { toast } from "../../components/ui/Toast";
import type { ClientSummary } from "../../lib/api/models";
import { can, useMe } from "../../lib/auth";
import { relativeTime } from "../../lib/format";
import { useClientSync, useClients, useSetClientTier } from "../../lib/queries";
import { useNow } from "../../lib/useNow";
import { groupByTier } from "./tier-groups";

const NUM = "text-right tabular-nums";
/** The select's value for a client without a tier: an option needs a value that is not empty. */
const NONE = "none";
const TIER_OPTIONS: SelectOption[] = [
  ...CLIENT_TIERS.map((t) => ({ value: t.key, label: t.label })),
  { value: NONE, label: UNCATEGORIZED_LABEL },
];

/**
 * An admin's choice of a client's tier. A change is sent at once; until its answer and the new list are in, the
 * select shows the chosen value and is disabled, so two changes of one client cannot cross. After a failure it
 * shows the client's own tier again, which is what the list holds.
 */
function TierSelect({ client, refocus }: { client: ClientSummary; refocus: MutableRefObject<string | null> }) {
  const setTier = useSetClientTier();
  const [pending, setPending] = useState<{ tier: ClientTier | null } | null>(null);
  const id = `tier-${client.id}`;
  // The focus follows the client: a row that moves under another heading mounts a new select, and a select that
  // stays (failed change, same group) is enabled again. Either takes the focus once nothing is pending.
  useEffect(() => {
    if (pending !== null || refocus.current !== client.id) return;
    document.getElementById(id)?.focus();
    // The new list may arrive just after this select is enabled again and move the row: its new select must still
    // find the claim. The claim is dropped soon after, so it cannot steal the focus later.
    const claimed = client.id;
    setTimeout(() => {
      if (refocus.current === claimed) refocus.current = null;
    }, 1000);
  }, [pending, refocus, client.id, id]);
  const change = async (value: string) => {
    const tier = value === NONE ? null : isClientTier(value) ? value : undefined;
    if (tier === undefined || tier === client.tier || pending) return;
    setPending({ tier });
    refocus.current = client.id;
    try {
      await setTier.mutateAsync({ id: client.id, tier });
      toast(`${client.name} moved to ${clientTierLabel(tier)}`, "success");
    } catch (e) {
      toast(e instanceof Error ? e.message : "The tier could not be changed.", "error");
    } finally {
      setPending(null);
    }
  };
  const shown = pending ? pending.tier : client.tier;
  return (
    <Select
      id={id}
      aria-label={`Tier of ${client.name}`}
      value={shown !== null && isClientTier(shown) ? shown : NONE}
      onChange={(value) => void change(value)}
      options={TIER_OPTIONS}
      disabled={pending !== null}
      className="h-8! w-48 text-sm"
    />
  );
}

/**
 * The client list, grouped by tier (client tiers spec §7.1): one table, a heading row per tier, so the columns
 * line up across the sections. Clients come from Gorelo, so the page has no action: a full sync is started under
 * Admin → Gorelo, and when the last one ran is quiet text beside the title (page frame spec §5.2). The one thing
 * set here is the tier, by an admin.
 */
export function ClientsPage() {
  const me = useMe();
  const { data: clients, isLoading } = useClients();
  const { data: sync } = useClientSync();
  const now = useNow();
  const admin = can(me, "admin");
  /** The client whose tier select takes the focus when it appears in its new row. */
  const refocus = useRef<string | null>(null);
  // An administrator also sees when Gorelo is not connected (Gorelo sync §6.1).
  const note =
    [
      admin && sync?.configured === false ? "Gorelo is not connected" : null,
      sync?.lastOkAt ? `Last synced ${relativeTime(sync.lastOkAt, new Date(now))}` : null,
    ]
      .filter(Boolean)
      .join(" · ") || null;
  const columns = admin ? 6 : 5;
  return (
    <>
      <PageBar>
        <Crumb current>Clients</Crumb>
      </PageBar>
      <Page>
        <PageTitle aside={note ? <span>{note}</span> : undefined}>Clients</PageTitle>
        {isLoading ? <Spinner label="Loading clients…" /> : null}
        {clients && clients.length === 0 ? (
          <EmptyState
            title="No clients yet"
            body="Clients come from Gorelo. A client made there appears here within ten minutes."
          />
        ) : null}
        {clients && clients.length > 0 ? (
          <Panel>
            <div className="overflow-x-auto">
              <table className={admin ? "kd-datatable min-w-[780px]" : "kd-datatable min-w-[560px]"}>
                <thead>
                  <tr>
                    <th>Client</th>
                    <th className="text-right">Sites</th>
                    <th className="text-right">Devices</th>
                    <th className="text-right">Network items</th>
                    <th className="text-right">Documents</th>
                    {admin ? <th>Tier</th> : null}
                  </tr>
                </thead>
                {groupByTier(clients).map((g) => (
                  <tbody key={g.key}>
                    <tr>
                      <th scope="rowgroup" colSpan={columns}>
                        {g.label} ({g.clients.length})
                      </th>
                    </tr>
                    {g.clients.map((c) => (
                      <tr key={c.id}>
                        <td>
                          <span className="flex flex-wrap items-center gap-2">
                            <Link
                              to="/clients/$clientId"
                              params={{ clientId: c.id }}
                              className="font-semibold text-ink hover:text-green-ink"
                            >
                              {c.name}
                            </Link>
                            {c.synced ? null : <span className="text-xs text-ink-3">Not linked to Gorelo</span>}
                            {c.restricted ? <Lock size={12} className="text-ink-3" aria-label="Restricted" /> : null}
                          </span>
                        </td>
                        <td className={NUM}>{c.counts.sites}</td>
                        <td className={NUM}>{c.counts.devices}</td>
                        <td className={NUM}>{c.counts.network}</td>
                        <td className={NUM}>{c.counts.documents}</td>
                        {admin ? (
                          <td>
                            <TierSelect client={c} refocus={refocus} />
                          </td>
                        ) : null}
                      </tr>
                    ))}
                  </tbody>
                ))}
              </table>
            </div>
          </Panel>
        ) : null}
      </Page>
    </>
  );
}
